import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Container,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
  IconButton,
  Tooltip
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import { snapshotIsVendor } from '../utils/employeeTypeUi';
import { keyframes } from '@emotion/react';
import { buildWebviewWebSocketUrl, buildWebviewFetchHeaders } from '../utils/webviewAudio';

// API endpoints + websocket helpers
const normalizeBaseUrl = (value) => {
  if (!value) return '';
  return value.endsWith('/') ? value.slice(0, -1) : value;
};

const resolveApiBase = () => {
  if (typeof window !== 'undefined' && window.location && window.location.origin) {
    const origin = window.location.origin;
    const url = new URL(origin);
    // In production (HTTPS), use same origin (reverse proxy handles routing)
    // In development (HTTP), use port 8000 for the backend API
    if (url.protocol === 'https:') {
      return normalizeBaseUrl(origin);
    }
    // Development: backend is on port 8000
    return normalizeBaseUrl(`${url.protocol}//${url.hostname}:8000`);
  }
  return normalizeBaseUrl('http://localhost:8000');
};
const API_BASE_URL = resolveApiBase();
const API_URL = `${API_BASE_URL}/admin/attendance/logs`;
const deriveWebsocketUrl = (laneNumber = null) => buildWebviewWebSocketUrl(laneNumber, false);
// Display duration - all cards vanish 5 seconds after last card arrives
const DISPLAY_DURATION = 9000;
const MAX_CARDS = 3; // Maximum 3 cards displayed
const SIDEBAR_LIMIT = parseInt(process.env.REACT_APP_SIDEBAR_LIMIT || '7', 10);

const pad2 = (value) => value.toString().padStart(2, '0');

const normalizeTimestampString = (value) => {
  if (!value) return null;
  let normalized = value.trim();
  if (!normalized) return null;
  // Replace space between date/time with T for ISO parsing
  if (normalized.includes(' ') && !normalized.includes('T')) {
    normalized = normalized.replace(' ', 'T');
  }
  // Ensure timezone info present; default to UTC if missing
  if (!/(z|Z|[+-]\d{2}:?\d{2})$/.test(normalized)) {
    normalized = `${normalized}${normalized.includes('T') ? '' : 'T'}${normalized.includes('T') ? '' : '00:00:00'}Z`;
  }
  // Trim microseconds to milliseconds for Date parsing
  const dotIndex = normalized.indexOf('.');
  if (dotIndex !== -1) {
    const zoneMatch = normalized.slice(dotIndex).match(/(Z|[+-]\d{2}:?\d{2})$/);
    const zone = zoneMatch ? zoneMatch[0] : '';
    const fraction = normalized.slice(dotIndex + 1, zoneMatch ? -zone.length : undefined);
    const millis = fraction.slice(0, 3).padEnd(3, '0');
    const beforeDot = normalized.slice(0, dotIndex);
    normalized = `${beforeDot}.${millis}${zone}`;
  }
  return normalized;
};

const parseTimestampValue = (value) => {
  if (value === null || value === undefined) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    const date = new Date(value > 1e12 ? value : value * 1000);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    if (/^\d{10,}$/.test(trimmed)) {
      const num = Number(trimmed);
      const date = new Date(num > 1e12 ? num : num * 1000);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const normalized = normalizeTimestampString(trimmed);
    if (!normalized) return null;
    const parsed = new Date(normalized);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
};

const formatTimestampLabel = (rawValue, fallbackDate) => {
  const parsed = parseTimestampValue(rawValue) || fallbackDate;
  if (!(parsed instanceof Date) || Number.isNaN(parsed.getTime())) {
    return '';
  }
  // Date Parts
  const day = pad2(parsed.getDate());
  const month = pad2(parsed.getMonth() + 1);
  const year = parsed.getFullYear();

  // Time Parts (12-hour)
  const hours24 = parsed.getHours();
  const period = hours24 >= 12 ? 'PM' : 'AM';
  let hours12 = hours24 % 12;
  if (hours12 === 0) hours12 = 12;
  const hours = pad2(hours12);
  const minutes = pad2(parsed.getMinutes());

  // Final Format: TIME FIRST, THEN DATE
  return `${hours}:${minutes} ${period} · ${day}/${month}/${year}`;
};

// Animations
const float = keyframes`
  0%, 100% { transform: translate(0px, 0px) rotate(0deg); }
  33% { transform: translate(30px, -30px) rotate(120deg); }
  66% { transform: translate(-20px, 20px) rotate(240deg); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.03); }
  100% { transform: scale(1); }
`;

const slideIn = keyframes`
  from { transform: translateY(30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
`;

const rotate = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const connectionPulse = keyframes`
  0% { box-shadow: 0 0 0 0 currentColor; }
  70% { box-shadow: 0 0 0 8px rgba(0, 0, 0, 0); }
  100% { box-shadow: 0 0 0 0 rgba(0, 0, 0, 0); }
`;

// Fallback avatar component
const toDataUrl = (value) => {
  if (!value) return null;
  if (value.startsWith('data:')) return value;
  return `data:image/jpeg;base64,${value}`;
};

const toLaneNumber = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  const parsed = parseInt(String(raw), 10);
  return Number.isNaN(parsed) || parsed <= 0 ? null : parsed;
};

const resolveLaneFromPathname = () => {
  if (typeof window === 'undefined' || !window.location) return null;
  const parts = String(window.location.pathname || '').split('/').filter(Boolean);
  const webviewIdx = parts.findIndex((p) => String(p).toLowerCase() === 'webview');
  if (webviewIdx < 0 || webviewIdx + 1 >= parts.length) return null;
  const rawPart = String(parts[webviewIdx + 1] || '');
  let decoded = rawPart;
  try {
    decoded = decodeURIComponent(rawPart);
  } catch (e) {
    decoded = rawPart;
  }
  const compact = decoded.toLowerCase().trim().replace(/\s+/g, '');
  const match = compact.match(/^lane(\d+)$/) || compact.match(/^lane-(\d+)$/) || compact.match(/^(\d+)$/);
  return match ? toLaneNumber(match[1]) : null;
};

// Updated avatar component: supports rectangular img fallback and rounded initials avatar
const UserAvatar = ({ user, rect = false, imgHeight = 180 }) => {
  const imageSrc = toDataUrl(user?.image);
  if (rect) {
    // rectangular image to fit left panel
    if (imageSrc) {
      return (
        <Box sx={{ width: '100%', height: imgHeight, overflow: 'hidden', borderRadius: 2, boxShadow: 3 }}>
          <Box
            component="img"
            src={imageSrc}
            alt={imageSrc}
            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
          />
        </Box>
      );
    }

    // fallback initials in a rectangle
    return (
      <Box sx={{ width: '100%', height: imgHeight, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2, bgcolor: 'primary.main', boxShadow: 3 }}>
        <Typography sx={{ color: '#fff', fontWeight: '700', fontSize: Math.max(18, Math.min(28, Math.floor(imgHeight / 6))) }}>
          {user?.name?.split(' ').map(n => n[0]).join('')}
        </Typography>
      </Box>
    );
  }

  // circular avatar for sidebar or compact places
  if (imageSrc) {
    return (
      <Avatar
        src={imageSrc}
        alt={imageSrc}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
    );
  } 
  return (
    <Avatar
      sx={{
        width: 56,
        height: 56,
        bgcolor: 'primary.main',
        fontSize: '1rem',
        border: '2px solid #fff',
        boxShadow: 3
      }}
    >
      {user?.name?.split(' ').map(n => n[0]).join('')}
    </Avatar>
  );
};

const WebViewRexnord = ({ laneNumber = null }) => {
  // preserved states
  const [currentEntry, setCurrentEntry] = useState(null);
  const [sidebarEntries, setSidebarEntries] = useState([]);

  // NEW: active main-screen cards (FIFO). newest at beginning (index 0).
  const [activeCards, setActiveCards] = useState([]);

  // Notification area visibility toggle - default OFF (hidden)
  const [showNotifications, setShowNotifications] = useState(false);

  // Company settings state - fetched from database (active company with is_active=true)
  const [companySettings, setCompanySettings] = useState({
    logo: '/Rexnord-logo.png', // Default logo
    description: 'Welcome to REXNORD\nElectronic & Controls Limited.' // Default message
  });
  const [isSocketConnected, setIsSocketConnected] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isBelow1300 = useMediaQuery('(max-width:1300px)');
  const isBelow1100 = useMediaQuery('(max-width:1100px)');
  const isBelow800 = useMediaQuery('(max-width:800px)');
  const isShortScreen = useMediaQuery('(max-height:700px)');

  const websocketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const dedupeTimersRef = useRef(new Map());

  // Single timer for vanishing all cards after 5 seconds from last card
  const vanishTimerRef = useRef(null);

  const resolvedLaneNumber = useMemo(() => {
    const fromProp = toLaneNumber(laneNumber);
    if (fromProp) return fromProp;
    return resolveLaneFromPathname();
  }, [laneNumber]);

  const matchesLane = useCallback((entry) => {
    if (!resolvedLaneNumber) return true;
    return toLaneNumber(entry?.laneNumber) === resolvedLaneNumber;
  }, [resolvedLaneNumber]);

  // helper to determine attendance event
  const isAttendanceEvent = (payload = {}) => {
    const eventType = (payload.event || '').toString().toLowerCase();
    const mode = (payload.mode || '').toString().toLowerCase();
    return eventType === 'attendance' || mode === 'attendance';
  };

  const buildDisplayEntry = (payload = {}, overrides = {}) => {
    const person = payload.person || {};
    const timestampValue = overrides.timestamp || payload.timestamp || payload.created_at;
    const parsedTimestamp = parseTimestampValue(timestampValue);
    const safeDate = parsedTimestamp || new Date();
    const rawAction = (overrides.action || payload.action || payload.type || '').toString().toLowerCase();
    const action = rawAction.includes('out') ? 'check_out' : 'check_in';
    const rawMode = (overrides.mode || payload.mode || (payload.event === 'attendance' ? 'attendance' : 'normal') || '').toString().toLowerCase();
    const mode = rawMode === 'attendance' ? 'attendance' : 'normal';
    const camera = (overrides.camera || payload.camera || (action === 'check_out' ? 'OUT' : 'IN') || '').toString().toUpperCase();
    const trackId = overrides.trackId || payload.track_id || payload.trackId || payload.trackID || payload.trackid;
    const name = overrides.name || payload.person_name || payload.name || person.name || 'Unknown Visitor';
    const personId = overrides.personId || payload.person_id || payload.id || person.person_id || `guest-${Date.now()}`;
    const employeeCode = overrides.employee_code || payload.employee_code || person.employee_code || null;
    const department = overrides.department || payload.department || person.department || 'General';
    const employeeType =
      overrides.employeeType ?? payload.employee_type ?? person.employee_type ?? null;
    const image = toDataUrl(overrides.image || payload.image || person.image || null);
    const metadata = payload.request_metadata && typeof payload.request_metadata === 'object'
      ? payload.request_metadata
      : {};
    const entryLaneNumber = toLaneNumber(
      overrides.laneNumber ??
      payload.lane_number ??
      payload.laneNumber ??
      metadata.lane_number ??
      metadata.laneNumber
    );

    return {
      id: payload.id || payload.attendance_id || `${personId}-${safeDate.getTime()}`,
      name,
      personId,
      employeeCode,
      trackId,
      action,
      mode,
      camera,
      image,
      department,
      employeeType,
      timestamp: safeDate.toISOString(),
      time: formatTimestampLabel(timestampValue, safeDate),
      modeLabel: mode === 'attendance' ? 'Attendance Marked' : 'Normal Check-In',
      actionLabel: action === 'check_out' ? 'Checked Out' : 'Checked In',
      directionText: camera ? `${camera} Camera` : (action === 'check_out' ? 'Exit Event' : 'Entry Event'),
      laneNumber: entryLaneNumber,
    };
  };

  const pushEntryToSidebar = useCallback((entry) => {
    if (!entry) return;

    setSidebarEntries(current => {
      // Remove duplicate with the same ID
      const filtered = current.filter(e => e.id !== entry.id);

      // Add new notification at top
      const updated = [entry, ...filtered];

      // FIFO LIMIT = SIDEBAR_LIMIT - 1 → remove oldest when limit reached
      if (updated.length > SIDEBAR_LIMIT) {
        return updated.slice(0, SIDEBAR_LIMIT);   // keep newest only
      }
      return updated;
    });
  }, []);

  const getEntryKey = (entry) => {
    if (!entry) return null;
    const pid = entry.personId != null ? String(entry.personId).trim() : '';
    const guestOrUnknown = !pid || pid.startsWith('guest-');
    if (!guestOrUnknown) {
      return `${pid}-${entry.action}`;
    }
    const rawTrack = entry.trackId;
    if (rawTrack !== undefined && rawTrack !== null && String(rawTrack).trim() !== '') {
      return `track-${rawTrack}`;
    }
    if (pid) {
      return `${pid}-${entry.action}`;
    }
    return null;
  };

  // dedupe logic preserved
  const shouldDisplayEntry = (entry) => {
    const key = getEntryKey(entry);
    if (!key) return true;
    const existing = dedupeTimersRef.current.get(key);
    if (existing) {
      return false;
    }
    const timeoutId = setTimeout(() => {
      dedupeTimersRef.current.delete(key);
    }, DISPLAY_DURATION);
    dedupeTimersRef.current.set(key, timeoutId);
    return true;
  };

  // Clear all cards and push them to sidebar
  const clearAllCards = useCallback(() => {
    setActiveCards(prev => {
      // Push all cards to sidebar
      prev.forEach(card => {
        pushEntryToSidebar(card.original || card);
      });
      return [];
    });
  }, [pushEntryToSidebar]);

  // Reset the vanish timer - called whenever a new card is added
  const resetVanishTimer = useCallback(() => {
    // Clear existing timer
    if (vanishTimerRef.current) {
      clearTimeout(vanishTimerRef.current);
    }
    // Set new timer - all cards vanish 5 seconds after last card
    vanishTimerRef.current = setTimeout(() => {
      clearAllCards();
    }, DISPLAY_DURATION);
  }, [clearAllCards]);

  // add or refresh card in activeCards
  // Newest card appears at top (index 0), oldest at bottom
  // Max 3 cards displayed
  const addOrRefreshCard = useCallback((entry) => {
    if (!entry) return;
    setActiveCards(prev => {
      // check if already present
      const existing = prev.find(c => c.id === entry.id);
      if (existing) {
        // Just update the timestamp, card stays in same position
        return prev.map(c => c.id === entry.id ? ({ ...c, addedAt: Date.now(), original: entry }) : c);
      }

      // Not present: if already MAX_CARDS (3), remove the oldest (last) and send to sidebar
      let next = [...prev];
      if (next.length >= MAX_CARDS) {
        const oldest = next.pop(); // remove last (oldest)
        if (oldest) {
          pushEntryToSidebar(oldest.original || oldest);
        }
      }

      // add new card at the beginning (so newest at top)
      const wrapped = { ...entry, addedAt: Date.now(), original: entry };
      next.unshift(wrapped);

      return next;
    });
    
    // Reset the vanish timer on every new card
    resetVanishTimer();
  }, [pushEntryToSidebar, resetVanishTimer]);

  // showEntry replaced/augmented to manage activeCards rather than single currentEntry only
  const showEntry = (entry) => {
    if (!entry) return;
    if (!shouldDisplayEntry(entry)) {
      return;
    }
    // add or refresh active card
    addOrRefreshCard(entry);
  };

  // whenever activeCards changes, keep currentEntry in-sync with the newest (first) card for backward compatibility
  useEffect(() => {
    if (activeCards.length === 0) {
      setCurrentEntry(null);
    } else {
      // show the latest added card as "currentEntry" (newest is at index 0)
      const first = activeCards[0];
      setCurrentEntry(first.original || first);
    }
  }, [activeCards]);

  // cleanup timers on unmount
  useEffect(() => {
    return () => {
      // clear vanish timer
      if (vanishTimerRef.current) clearTimeout(vanishTimerRef.current);
      // clear dedupe timers
      dedupeTimersRef.current.forEach(t => clearTimeout(t));
      dedupeTimersRef.current.clear();
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (websocketRef.current) {
        try { websocketRef.current.close(); } catch (e) { }
        websocketRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // load initial logs (kept original)
  useEffect(() => {
    let isMounted = true;
    const loadInitialLogs = async () => {
      try {
        const response = await fetch(API_URL);
        if (!response.ok) return;
        const payload = await response.json();
        const logs = Array.isArray(payload) ? payload : payload.logs || [];
        if (!isMounted) return;
        const formatted = logs
          .map(log => buildDisplayEntry(log, { mode: 'attendance' }))
          .filter(matchesLane)
          .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        setSidebarEntries(formatted);
      } catch (error) {
        console.error('Error fetching attendance logs:', error);
      }
    };

    loadInitialLogs();

    return () => {
      isMounted = false;
    };
  }, [matchesLane]);

  // Fetch company settings on mount - gets active company (is_active=true) from database
  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        // No company_id parameter - API returns the active company (is_active=true)
        const apiUrl = `${API_BASE_URL}/api/v1/settings/company`;
        // console.log('[WebView] Fetching company settings from:', apiUrl);
        
        const response = await fetch(apiUrl, { headers: buildWebviewFetchHeaders() });
        // console.log('[WebView] Company settings response status:', response.status);
        
        if (!response.ok) {
          console.error('[WebView] Failed to fetch company settings. Status:', response.status);
          return;
        }
        
        const data = await response.json();
        // console.log('[WebView] Fetched active company settings:', data);
        // console.log('[WebView] Logo value from API:', data.logo, '| Type:', typeof data.logo);
        // console.log('[WebView] Description value from API:', data.description, '| Type:', typeof data.description);
        
        // Use values from API, only fall back to defaults if truly empty/null/undefined
        const logoValue = data.logo && data.logo.trim() !== '' ? data.logo : '/Rexnord-logo.png';
        const descriptionValue = data.description && data.description.trim() !== '' ? data.description : 'Welcome to REXNORD\nElectronic & Controls Limited.';
        
        // console.log('[WebView] Final logo value:', logoValue);
        // console.log('[WebView] Final description value:', descriptionValue);
        
        setCompanySettings({
          logo: logoValue,
          description: descriptionValue
        });
      } catch (error) {
        console.error('[WebView] Error fetching company settings:', error);
        // Keep using defaults on error
      }
    };

    fetchCompanySettings();
  }, []);

  // websocket connect (kept original, now pushes to showEntry)
  useEffect(() => {
    const connectWebsocket = () => {
      try {
        if (websocketRef.current) {
          websocketRef.current.close();
        }
        const socket = new WebSocket(deriveWebsocketUrl(resolvedLaneNumber));
        websocketRef.current = socket;

        socket.onopen = () => {
          setIsSocketConnected(true);
        };

        socket.onmessage = (event) => {
          try {
            const payload = JSON.parse(event.data);
            if (!isAttendanceEvent(payload)) {
              return;
            }
            const entry = buildDisplayEntry(payload, { mode: 'attendance' });
            if (entry.mode !== 'attendance') {
              return;
            }
            if (!matchesLane(entry)) {
              return;
            }
            showEntry(entry);
          } catch (err) {
            console.error('Invalid websocket payload:', err);
          }
        };

        socket.onclose = () => {
          websocketRef.current = null;
          setIsSocketConnected(false);
          if (reconnectTimerRef.current) {
            clearTimeout(reconnectTimerRef.current);
          }
          reconnectTimerRef.current = setTimeout(connectWebsocket, 4000);
        };

        socket.onerror = (err) => {
          console.error('Websocket error:', err);
          setIsSocketConnected(false);
          try { socket.close(); } catch (e) { }
        };
      } catch (error) {
        console.error('Failed to initialize websocket:', error);
      }
    };

    connectWebsocket();

    return () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
      }
      if (websocketRef.current) {
        try { websocketRef.current.close(); } catch (e) { }
        websocketRef.current = null;
      }
      setIsSocketConnected(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchesLane]);

  // Background styles
  const backgroundStyles = {
    position: 'fixed',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: `
      radial-gradient(circle at 20% 80%, rgba(64, 81, 138, 0.15) 0%, transparent 30%),
      radial-gradient(circle at 80% 20%, rgba(64, 81, 138, 0.10) 0%, transparent 30%),
      radial-gradient(circle at 40% 40%, rgba(64, 81, 138, 0.08) 0%, transparent 30%)
    `,
    animation: `${float} 20s ease-in-out infinite`,
    zIndex: -1,
  };

  // visibleSidebarEntries excludes any currently active on main screen
  const visibleSidebarEntries = sidebarEntries.filter(
    entry => !activeCards.some(c => c.id === entry.id)
  );

  // Render helpers: fully responsive card for mobile, tablet, and laptop
  const renderOriginalCard = (entry, compact = false) => {
    return (
      <Card
        key={entry.id}
        sx={{
          width: '100%',
          height: '100%',
          maxWidth: '100%',
          mx: 0,
          px: { xs: 0.5, sm: 1, md: 1.5 },
          py: { xs: 0.5, sm: 1, md: 1.5 },
          borderRadius: { xs: 2, sm: 2.5, md: 3 },
          boxShadow: '0 10px 30px rgba(64, 81, 138, 0.2)',
          animation: `${slideIn} 0.5s ease`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'stretch',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          '&:hover': {
            transform: { xs: 'none', md: 'translateY(-2px)' },
            boxShadow: { xs: '0 10px 30px rgba(64, 81, 138, 0.2)', md: '0 12px 35px rgba(64, 81, 138, 0.25)' },
          }
        }}
      >
        {/* Left image section: Responsive width */}
        <Box sx={{
          width: { xs: '25%', sm: '22%', md: compact ? '19%' : '20%' },
          minWidth: { xs: '25%', sm: '22%', md: compact ? '19%' : '20%' },
          maxWidth: { xs: '25%', sm: '22%', md: compact ? '19%' : '20%' },
          background: 'linear-gradient(135deg, #40518A 0%, #5a6fa8 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}>
          {/* Image always displayed */}
          {entry.image ? (
            <Box
              component="img"
              src={entry.image}
              alt={entry.image}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center',
                display: 'block'
              }}
            />
          ) : (
            <Box sx={{ 
              width: '100%', 
              height: '100%', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              bgcolor: 'primary.main' 
            }}>
              <Typography sx={{ 
                color: '#fff', 
                fontWeight: '700', 
                fontSize: { xs: '1rem', sm: '1.5rem', md: compact ? '1.8rem' : '3rem' }
              }}>
                {entry?.name?.split(' ').map(n => n[0]).join('')}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right content section: Responsive padding and fonts */}
        <CardContent sx={{ 
          flex: 1, 
          p: { xs: 1, sm: 1.5, md: compact ? 1.5 : 2.5 },
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center',
          overflow: 'hidden',
          background: 'linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(250,250,255,1) 100%)'
        }}>
          <Stack 
            direction="row" 
            spacing={{ xs: 0.5, sm: 0.75, md: 1 }}
            sx={{ 
              mb: { xs: 0.5, sm: 1, md: 1.5 },
              flexWrap: 'wrap', 
              alignItems: 'center', 
              gap: { xs: 0.5, sm: 0.75, md: 1 }
            }}
          >
            <Chip
              label={entry.actionLabel}
              color={entry.action === 'check_out' ? 'warning' : 'success'}
              size={isMobile ? 'small' : 'medium'}
              sx={{ 
                paddingTop: { xs: 0.2, sm: 0.3, md: 0.5 },
                height: { xs: 24, sm: 32, md: compact ? 38 : 45 },
                borderRadius: { xs: 1.5, md: 2 },
                textTransform: 'uppercase', 
                fontWeight: 'bold', 
                letterSpacing: { xs: 0.3, md: 0.8 },
                fontSize: { xs: '0.55rem', sm: '0.75rem', md: compact ? '0.9rem' : '1.2rem' },
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '& .MuiChip-label': { 
                  px: { xs: 0.75, sm: 1, md: 2 }, 
                  whiteSpace: 'nowrap', 
                  lineHeight: 1 
                } 
              }}
            />
            {entry.directionText && (
              <Chip
                label={entry.directionText}
                color="info"
                size={isMobile ? 'small' : 'medium'}
                sx={{ 
                  paddingTop: { xs: 0.2, sm: 0.3, md: 0.5 },
                  height: { xs: 24, sm: 32, md: compact ? 38 : 45 },
                  borderRadius: { xs: 1.5, md: 2 },
                  textTransform: 'uppercase', 
                  fontWeight: 'bold', 
                  letterSpacing: { xs: 0.3, md: 0.8 },
                  fontSize: { xs: '0.55rem', sm: '0.75rem', md: compact ? '0.9rem' : '1.1rem' },
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  '& .MuiChip-label': { 
                    px: { xs: 0.75, sm: 1, md: 2 }, 
                    whiteSpace: 'nowrap', 
                    lineHeight: 1 
                  } 
                }}
              />
            )}
            {snapshotIsVendor(entry.employeeType) && (
              <Tooltip title="Vendor" arrow>
                <Chip
                  icon={<AssignmentIndIcon sx={{ fontSize: { xs: '0.95rem !important', md: '1.05rem !important' }, color: 'white !important' }} />}
                  label="Vendor"
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    paddingTop: { xs: 0.2, sm: 0.3, md: 0.5 },
                    height: { xs: 24, sm: 32, md: compact ? 38 : 45 },
                    fontWeight: 'bold',
                    bgcolor: 'success.main',
                    color: '#fff',
                    '& .MuiChip-icon': { color: '#fff' },
                  }}
                />
              </Tooltip>
            )}
          </Stack>

          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: '1.3rem', sm: '1.7rem', md: compact ? '2.2rem' : '2.8rem', lg: compact ? '2.6rem' : '3.5rem' },
              fontWeight: '800',
              color: '#40518A',
              mb: { xs: 0.2, sm: 0.3, md: 0.5 },
              lineHeight: 1.15,
              wordBreak: 'break-word',
              textShadow: '0 1px 2px rgba(64, 81, 138, 0.1)'
            }}
          >
            {entry.name}
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              color: 'primary.main',
              mt: { xs: 0.1, md: 0.3 },
              mb: { xs: 0.2, sm: 0.3, md: 0.5 },
              fontWeight: 700,
              fontSize: { xs: '0.75rem', sm: '0.95rem', md: compact ? '1.15rem' : '1.5rem', lg: compact ? '1.4rem' : '1.8rem' },
              letterSpacing: 0.3
            }}
          >
            Employee Code: {entry.employeeCode}
          </Typography>

          <Typography
            variant="h6"
            sx={{
              color: 'text.secondary',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.3, md: 0.5 },
              fontSize: { xs: '0.7rem', sm: '0.9rem', md: compact ? '1.05rem' : '1.35rem', lg: compact ? '1.25rem' : '1.6rem' },
            }}
          >
            🕐 {entry.time}
          </Typography>
        </CardContent>
      </Card>
    );
  };

  // Main render
  return (
    <Box sx={{
      minHeight: '100vh',
      '@supports (height: 100dvh)': {
        minHeight: '100dvh',
      },
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      color: '#40518A',
      position: 'relative',
      overflowX: 'hidden',
      overflowY: { xs: 'auto', md: 'hidden' },
      '&::before': backgroundStyles,
      '&::after': {
        content: '""',
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(circle at 50% 50%, rgba(64, 81, 138, 0.1) 0%, transparent 70%)',
        pointerEvents: 'none',
        zIndex: 0
      }
    }}>
      {/* Main Container - Responsive */}
      <Container
        maxWidth="xl"
        sx={{
          minHeight: '100vh',
          height: { xs: 'auto', md: '100vh' },
          '@supports (height: 100dvh)': {
            minHeight: '100dvh',
            height: { xs: 'auto', md: '100dvh' },
          },
          py: { xs: 1, sm: 1.5, md: 2 },
          position: 'relative',
          zIndex: 1
        }}
      >
        <Stack
          direction={isMobile ? 'column' : 'row'}
          spacing={{ xs: 1, sm: 1.5, md: 2 }}
          sx={{ height: { xs: 'auto', md: '100%' } }}
        >
          {/* Main Display Area - Full width when notifications hidden */}
          <Paper
            elevation={24}
            sx={{
              flex: 1,
              width: showNotifications ? 'auto' : '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              borderRadius: { xs: 2, sm: 3, md: 4 },
              p: { xs: 1, sm: 1.5, md: 2 },
              position: 'relative',
              transition: 'all 0.5s ease',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backgroundImage: 'radial-gradient(circle at 20% 50%, rgba(64, 81, 138, 0.03) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(64, 81, 138, 0.03) 0%, transparent 50%)',
                zIndex: 0,
              }
            }}
          >
            {/* Socket Connection Indicator - Responsive */}
            <Box
              sx={{
                position: 'absolute',
                bottom: { xs: 10, sm: 15, md: 20 },
                right: { xs: 10, sm: 15, md: 20 },
                width: { xs: 32, sm: 38, md: 45 },
                height: { xs: 32, sm: 38, md: 45 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                zIndex: 1000,
                border: '2px solid rgba(64, 81, 138, 0.1)',
              }}
            >
              <Box
                component="span"
                sx={{
                  width: { xs: 10, sm: 12, md: 14 },
                  height: { xs: 10, sm: 12, md: 14 },
                  borderRadius: '50%',
                  color: isSocketConnected ? '#2ecc71' : '#e74c3c',
                  backgroundColor: 'currentColor',
                  animation: `${connectionPulse} 1.8s ease-out infinite`,
                }}
                aria-label={isSocketConnected ? 'Socket connected' : 'Socket disconnected'}
              />
            </Box>

            {/* Show Notifications Button - Only visible when notifications are hidden */}
            {!showNotifications && (
              <Tooltip title="Show Notifications">
                <IconButton
                  onClick={() => setShowNotifications(true)}
                  sx={{
                    position: 'absolute',
                    top: { xs: 10, sm: 15, md: 20 },
                    right: { xs: 10, sm: 15, md: 20 },
                    width: { xs: 36, sm: 42, md: 50 },
                    height: { xs: 36, sm: 42, md: 50 },
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    color: '#40518A',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    zIndex: 1000,
                    border: '2px solid rgba(64, 81, 138, 0.1)',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(64, 81, 138, 0.1)',
                      transform: 'scale(1.05)',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.5rem' }
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              </Tooltip>
            )}
            <Box sx={{ position: 'relative', zIndex: 1, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
              {/* Page Header with Logo - Shows when cards are present - Responsive */}
              {(activeCards.length > 0 || currentEntry) && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: { xs: 8, sm: 12, md: 16 },
                    left: { xs: 8, sm: 12, md: 16 },
                    p: { xs: 0.75, sm: 1, md: 1.5 },
                    borderRadius: { xs: 1.5, md: 2 },
                    backdropFilter: 'blur(10px)',
                    animation: `${pulse} 2s infinite`,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    bgcolor: 'rgba(255,255,255,0.9)',
                  }}
                >
                  <Box
                    component="img"
                    src={companySettings.logo}
                    alt="Company Logo"
                    sx={{
                      height: { xs: 20, sm: 28, md: 35 },
                      width: 'auto',
                      borderRadius: 1,
                      objectFit: 'contain'
                    }}
                  />
                </Box>
              )}

              {/* Dynamic Attendance Cards Rendering - Max 3 cards in vertical list */}
              {(() => {
                const cards = activeCards.map(c => c.original || c);
                const count = cards.length;

                // 0 cards → Welcome - Responsive
                if (count === 0) {
                  return (
                    <Box
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center'
                      }}
                    >

                      {/* Always Visible Logo in Top-Left - Responsive */}
                      <Box
                        sx={{
                          position: 'absolute',
                          top: { xs: 10, sm: 15, md: 20 },
                          left: { xs: 10, sm: 15, md: 20 },
                          p: { xs: 0.75, sm: 1, md: 1.5 },
                        }}
                      >
                        <Box
                          component="img"
                          src={companySettings.logo}
                          alt="Company Logo"
                          sx={{
                            height: { xs: 35, sm: 45, md: 60 },
                            width: 'auto',
                            borderRadius: 1,
                            objectFit: 'contain'
                          }}
                        />
                      </Box>

                      {/* Welcome Message - Responsive */}
                      <Box
                        sx={{
                          textAlign: 'center',
                          animation: `${pulse} 2s infinite`,
                          px: { xs: 2, sm: 3, md: 4 }
                        }}
                      >
                        <Typography
                          variant="h1"
                          sx={{
                            fontSize: { xs: '1.3rem', sm: '2rem', md: '3rem', lg: '3.8rem' },
                            color: '#40518A',
                            mb: { xs: 1, md: 2 },
                            textShadow: '0 3px 15px rgba(64, 81, 138, 0.2)',
                            fontWeight: '900',
                            whiteSpace: 'pre-line',
                            letterSpacing: { xs: 0.5, md: 1 }
                          }}
                        >
                          {companySettings.description}
                        </Typography>
                      </Box>

                    </Box>
                  );
                }
                
                // 1 card → single card centered (responsive)
                if (count === 1) {
                  return (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column',
                      justifyContent: 'center', 
                      alignItems: 'center',
                      width: '100%',
                      height: { xs: '25%', sm: '30%', md: '35%' },
                      px: { xs: 1, sm: 2, md: 3 }
                    }}>
                      <Box sx={{ 
                        width: { xs: '95%', sm: '90%', md: '85%' }, 
                        maxWidth: { xs: 500, sm: 700, md: 1000 }, 
                        height: '100%' 
                      }}>
                        {renderOriginalCard(cards[0])}
                      </Box>
                    </Box>
                  );
                }
                
                // 2 cards → vertical stack (single column, 2 rows) - responsive
                // Layout: [ Card 1 (newest) ]
                //         [ Card 2 (older) ]
                if (count === 2) {
                  return (
                    <Box sx={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      justifyContent: 'center',
                      alignItems: 'center', 
                      gap: { xs: 0.75, sm: 1, md: 1.5 },
                      width: '100%',
                      height: { xs: '50%', sm: '55%', md: '65%' },
                      px: { xs: 1, sm: 2, md: 3 },
                      py: { xs: 0.5, sm: 1, md: 1.5 }
                    }}>
                      {cards.map(entry => (
                        <Box key={entry.id} sx={{ 
                          width: { xs: '95%', sm: '90%', md: '85%' }, 
                          maxWidth: { xs: 500, sm: 700, md: 1000 }, 
                          height: 'calc(50% - 8px)' 
                        }}>
                          {renderOriginalCard(entry, true)}
                        </Box>
                      ))}
                    </Box>
                  );
                }

                // 3 cards → vertical stack (single column, 3 rows) - responsive
                // Layout: [ Card 1 (newest) ]
                //         [ Card 2 ]
                //         [ Card 3 (oldest) ]
                return (
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    justifyContent: 'center',
                    alignItems: 'center', 
                    gap: { xs: 0.5, sm: 0.75, md: 1.5 },
                    width: '100%',
                    height: { xs: '85%', sm: '88%', md: '90%' },
                    px: { xs: 1, sm: 2, md: 3 },
                    py: { xs: 0.5, sm: 1, md: 1.5 }
                  }}>
                    {cards.slice(0, MAX_CARDS).map(entry => (
                      <Box key={entry.id} sx={{ 
                        width: { xs: '95%', sm: '92%', md: '88%' }, 
                        maxWidth: { xs: 500, sm: 700, md: 1000 }, 
                        height: 'calc(33.33% - 8px)' 
                      }}>
                        {renderOriginalCard(entry, true)}
                      </Box>
                    ))}
                  </Box>
                );
              })()}
            </Box>
          </Paper>

          {/* Notification Area - Conditionally rendered based on toggle (default OFF) - Responsive */}
          {showNotifications && (
          <Paper
            elevation={8}
            sx={{
              width: { xs: '100%', md: '25%', lg: '23%' },
              minWidth: { xs: 'auto', sm: 300, md: 340, lg: 380 },
              maxWidth: { xs: '100%', md: 420, lg: 480 },
              maxHeight: { xs: '40vh', md: '100%' },
              p: { xs: 1.5, sm: 2, md: 2.5 },
              borderRadius: { xs: 2, sm: 3, md: 4 },
              background: 'linear-gradient(135deg, rgba(64, 81, 138, 0.95) 0%, rgba(90, 111, 168, 0.95) 100%)',
              backdropFilter: 'blur(15px)',
              overflow: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.3)',
              animation: `${slideIn} 0.3s ease`,
              '&::-webkit-scrollbar': {
                width: { xs: '4px', md: '8px' },
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255,255,255,0.1)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(255,255,255,0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(255,255,255,0.4)',
                }
              }
            }}
          >
            {/* Close Button - Inside Notification Section */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Tooltip title="Hide Notifications">
                <IconButton
                  onClick={() => setShowNotifications(false)}
                  sx={{
                    width: { xs: 32, sm: 36, md: 40 },
                    height: { xs: 32, sm: 36, md: 40 },
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#fff',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.3)',
                      transform: 'scale(1.05)',
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: { xs: '1.2rem', sm: '1.3rem', md: '1.5rem' }
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Stack spacing={{ xs: 1.5, sm: 2, md: 2.5 }}>
              {visibleSidebarEntries.map((entry) => (
                <Paper
                  key={entry.id}
                  elevation={6}
                  sx={{
                    p: { xs: 1.5, sm: 2, md: 2.5 },
                    borderRadius: { xs: 2, md: 3 },
                    borderLeft: `${isMobile ? '3px' : '5px'} solid ${entry.action === 'check_out'
                      ? theme.palette.warning.main
                      : theme.palette.success.main
                      }`,
                    animation: `${slideIn} 0.5s ease`,
                    background: 'rgba(255, 255, 255, 0.98)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: { xs: 'none', md: 'translateX(-5px)' },
                      boxShadow: { xs: 'none', md: '0 6px 20px rgba(0,0,0,0.15)' },
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, md: 2 }, mb: { xs: 1, md: 1.8 } }}>
                    <Box
                      sx={{
                        width: { xs: 50, sm: 60, md: 70 },
                        aspectRatio: '2.5 / 4',
                        borderRadius: { xs: 1.5, md: 2 },
                        overflow: 'hidden',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        backgroundColor: 'grey.200',
                        border: '2px solid rgba(64, 81, 138, 0.1)',
                      }}
                    >
                      {entry.image ? (
                        <Box
                          component="img"
                          src={entry.image}
                          alt={entry.image}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <Box sx={{ 
                          width: '100%', 
                          height: '100%', 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'center', 
                          bgcolor: 'primary.main' 
                        }}>
                          <Typography sx={{ 
                            color: '#fff', 
                            fontWeight: '700', 
                            fontSize: { xs: '1rem', sm: '1.5rem', md: '2rem' }
                          }}>
                            {entry?.name?.split(' ').map(n => n[0]).join('')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography 
                        variant="h6" 
                        sx={{ 
                          fontSize: { xs: '0.95rem', sm: '1.2rem', md: '1.5rem', lg: '1.75rem' },
                          fontWeight: '800',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          mb: { xs: 0.25, md: 0.5 },
                          color: '#2c3e50'
                        }}
                      >
                        {entry.name}
                      </Typography>
                      {entry.employeeCode && (
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: 'primary.main', 
                            fontWeight: 700, 
                            fontSize: { xs: '0.75rem', sm: '0.95rem', md: '1.1rem', lg: '1.3rem' },
                            mb: { xs: 0.15, md: 0.3 }
                          }}
                        >
                          Code: {entry.employeeCode}
                        </Typography>
                      )}
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: 'primary.main',
                          fontSize: { xs: '0.7rem', sm: '0.9rem', md: '1rem', lg: '1.2rem' },
                          fontWeight: 700
                        }}
                      >
                        {entry.time}
                      </Typography>
                    </Box>
                  </Box>
                  <Stack 
                    direction="row" 
                    spacing={{ xs: 0.5, sm: 0.75, md: 1 }}
                    sx={{ flexWrap: 'wrap', gap: { xs: 0.5, md: 0.75 } }}
                  >
                    <Chip
                      label={entry.actionLabel}
                      color={entry.action === 'check_out' ? 'warning' : 'success'}
                      size="small"
                      sx={{
                        paddingTop: 0,
                        height: { xs: 20, sm: 22, md: 24 },
                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                        fontWeight: 700,
                        '& .MuiChip-label': {
                          fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.7rem' },
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          px: { xs: 0.5, sm: 0.75, md: 1 },
                          letterSpacing: 0.3
                        }
                      }}
                    />
                    <Chip
                      label={entry.directionText}
                      color="info"
                      size="small"
                      sx={{
                        paddingTop: 0,
                        height: { xs: 20, sm: 22, md: 24 },
                        boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                        fontWeight: 700,
                        '& .MuiChip-label': {
                          fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.7rem' },
                          fontWeight: 700,
                          textTransform: 'uppercase',
                          px: { xs: 0.5, sm: 0.75, md: 1 },
                          letterSpacing: 0.3
                        }
                      }}
                    />
                    {snapshotIsVendor(entry.employeeType) ? (
                      <Tooltip title="Vendor" arrow>
                        <Chip
                          icon={<AssignmentIndIcon sx={{ fontSize: '0.75rem !important', color: 'white !important' }} />}
                          label="Vendor"
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor: 'success.main',
                            color: '#fff',
                            height: { xs: 20, sm: 22, md: 24 },
                            '& .MuiChip-icon': { color: '#fff' },
                            '& .MuiChip-label': {
                              fontSize: { xs: '0.55rem', sm: '0.6rem', md: '0.7rem' },
                              px: { xs: 0.5, sm: 0.75 },
                            },
                          }}
                        />
                      </Tooltip>
                    ) : null}
                  </Stack>
                </Paper>
              ))}
              {visibleSidebarEntries.length === 0 && (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: { xs: 4, sm: 6, md: 8 },
                  px: { xs: 1, md: 2 }
                }}>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      color: 'white', 
                      fontSize: { xs: '0.9rem', sm: '1rem', md: '1.15rem' },
                      fontWeight: 600,
                      opacity: 0.9,
                      mb: { xs: 0.5, md: 1 }
                    }}
                  >
                    No Recent Activity
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: 'rgba(255,255,255,0.7)', 
                      fontSize: { xs: '0.75rem', sm: '0.85rem', md: '0.95rem' }
                    }}
                  >
                    Attendance logs will appear here
                  </Typography>
                </Box>
              )}
            </Stack>
          </Paper>
          )}
        </Stack>
      </Container>

    </Box>
  );
};

export default WebViewRexnord;